import { useEffect, useState } from 'react';
import { Word } from '../../hooks/useWords';
import { getApiErrorMessage } from '../../services/api';
import { Pencil, Trash2, Volume2, Image, Languages, Play, RefreshCw, Check, X } from 'lucide-react';
import { ConfirmDialog } from '../shared/ConfirmDialog';

interface WordRowProps {
  word: Word;
  index: number;
  onUpdate: (id: number, data: Partial<Word>) => void | Promise<void>;
  onDelete: (id: number) => void;
  onGenerate: (word: Word, type: 'translation' | 'audio' | 'image' | 'all') => void;
  isSelected: boolean;
  onSelect: (id: number, selected: boolean) => void;
}

interface WordForm {
  turkish: string;
  russian: string;
  transcription: string;
}

const toForm = (word: Word): WordForm => ({
  turkish: word.turkish,
  russian: word.russian || '',
  transcription: word.transcription || '',
});

const EDIT_HINT = 'Enter или Ctrl+Enter — сохранить, Esc — отменить';

export function WordRow({ word, index, onUpdate, onDelete, onGenerate, isSelected, onSelect }: WordRowProps) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<WordForm>(() => toForm(word));
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Props are the source of truth. Without this resync the form keeps the values it was
  // mounted with, so after a generation run the parent refetches fresh translations and
  // saving the row would overwrite them with stale pre-generation data.
  const { turkish, russian, transcription } = word;

  useEffect(() => {
    setForm({
      turkish,
      russian: russian || '',
      transcription: transcription || '',
    });
  }, [turkish, russian, transcription]);

  const startEditing = () => {
    setForm(toForm(word));
    setSaveError(null);
    setEditing(true);
  };

  const handleCancel = () => {
    setForm(toForm(word));
    setSaveError(null);
    setEditing(false);
  };

  // Saving is always explicit: a blur used to fire it, which meant clicking from the
  // Turkish field to the Russian field saved and unmounted the inputs mid-edit.
  const handleSave = async () => {
    if (saving) return;
    if (!form.turkish.trim()) {
      setSaveError('Турецкое слово не может быть пустым');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await onUpdate(word.id, {
        turkish: form.turkish.trim(),
        russian: form.russian.trim() || null,
        transcription: form.transcription.trim() || null,
      });
      setEditing(false);
    } catch (err: unknown) {
      // Stay in edit mode so the typed values are not lost.
      setSaveError(getApiErrorMessage(err, 'Не удалось сохранить слово'));
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      void handleSave();
    }
  };

  const inputClass =
    'w-full rounded border border-gray-300 px-2 py-1 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500';

  const statusBadge = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'bg-gray-100 text-gray-600',
      generating: 'bg-amber-50 text-amber-600',
      done: 'bg-green-50 text-green-600',
      error: 'bg-red-50 text-red-600',
    };
    const labels: Record<string, string> = {
      pending: 'Ожидание',
      generating: 'Генерация',
      done: 'Готово',
      error: 'Ошибка',
    };
    return (
      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${colors[status] || colors.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  return (
    <>
    <tr className="hover:bg-50">
      <td className="px-4 py-3">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={(e) => onSelect(word.id, e.target.checked)}
          className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
        />
      </td>
      <td className="px-4 py-3 text-gray-500">{index + 1}</td>
      <td className="px-4 py-3">
        {editing ? (
          <input
            value={form.turkish}
            onChange={(e) => setForm({ ...form, turkish: e.target.value })}
            onKeyDown={handleKeyDown}
            className={inputClass}
            aria-label="Турецкое слово"
            autoFocus
          />
        ) : (
          <span className="font-medium text-gray-900">{word.turkish}</span>
        )}
      </td>
      <td className="px-4 py-3">
        {editing ? (
          <input
            value={form.russian}
            onChange={(e) => setForm({ ...form, russian: e.target.value })}
            onKeyDown={handleKeyDown}
            className={inputClass}
            aria-label="Перевод"
          />
        ) : (
          <span className="text-gray-700">{word.russian || '—'}</span>
        )}
      </td>
      <td className="px-4 py-3">
        {editing ? (
          <input
            value={form.transcription}
            onChange={(e) => setForm({ ...form, transcription: e.target.value })}
            onKeyDown={handleKeyDown}
            className={inputClass}
            aria-label="Транскрипция"
          />
        ) : (
          <span className="text-gray-500">{word.transcription || '—'}</span>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          {word.audioStatus === 'done' && word.audioPath ? (
            <button
              onClick={() => new Audio(`/data/media/${word.audioPath}`).play()}
              className="rounded p-1 text-brand-600 hover:bg-brand-50"
            >
              <Play size={14} />
            </button>
          ) : (
            <Volume2 size={14} className={word.audioStatus === 'error' ? 'text-red-400' : 'text-gray-300'} />
          )}
          {statusBadge(word.audioStatus)}
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          {word.imageStatus === 'done' && word.imagePath ? (
            <img
              src={`/data/media/${word.imagePath}`}
              alt={word.turkish}
              className="h-8 w-8 rounded object-cover"
            />
          ) : (
            <Image size={14} className={word.imageStatus === 'error' ? 'text-red-400' : 'text-gray-300'} />
          )}
          {statusBadge(word.imageStatus)}
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <Languages size={14} className={word.translationStatus === 'done' ? 'text-green-500' : word.translationStatus === 'error' ? 'text-red-400' : 'text-gray-300'} />
          {statusBadge(word.translationStatus)}
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1">
          {editing ? (
            <>
              <button
                onClick={() => void handleSave()}
                disabled={saving}
                title={EDIT_HINT}
                aria-label="Сохранить"
                className="rounded p-1 text-brand-600 hover:bg-brand-50 disabled:opacity-50"
              >
                <Check size={14} />
              </button>
              <button
                onClick={handleCancel}
                title="Отменить изменения"
                aria-label="Отменить"
                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <X size={14} />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => onGenerate(word, 'translation')}
                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-brand-600"
                title="Перевод"
              >
                <Languages size={14} />
              </button>
              <button
                onClick={() => onGenerate(word, 'audio')}
                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-brand-600"
                title="Аудио"
              >
                <Volume2 size={14} />
              </button>
              <button
                onClick={() => onGenerate(word, 'image')}
                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-brand-600"
                title="Изображение"
              >
                <Image size={14} />
              </button>
              <button
                onClick={() => onGenerate(word, 'all')}
                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-brand-600"
                title="Всё"
              >
                <RefreshCw size={14} />
              </button>
              <button
                onClick={startEditing}
                title="Редактировать"
                aria-label="Редактировать"
                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-brand-600"
              >
                <Pencil size={14} />
              </button>
            </>
          )}
          <button
            onClick={() => setConfirmDelete(true)}
            title="Удалить"
            aria-label="Удалить"
            className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
          >
            <Trash2 size={14} />
          </button>
        </div>
        {saveError && <p className="mt-1 text-xs text-red-600">{saveError}</p>}
      </td>
    </tr>
      <ConfirmDialog
        isOpen={confirmDelete}
        title="Удалить слово?"
        message={`Слово «${word.turkish}» будет удалено безвозвратно.`}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => {
          setConfirmDelete(false);
          onDelete(word.id);
        }}
      />
    </>
  );
}